import React, { useState } from 'react';
import { Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import ExcelJS from 'exceljs';

const ExportarCSV = ({ 
  data, 
  participantesFiltrados, 
  periodosOrdenados, 
  metricas,
  filtrosActivos 
}) => {
  const [exporting, setExporting] = useState(false);

  const exportarAExcel = async () => {
    setExporting(true);
    
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Reporte Participantes');
      
      // Configurar estilos
      const estiloTitulo = {
        font: { bold: true, size: 16, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '2C3E50' } },
        alignment: { horizontal: 'center', vertical: 'middle' }
      };

      const estiloSubTitulo = {
        font: { bold: true, size: 12 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'ECF0F1' } },
        alignment: { horizontal: 'left', vertical: 'middle' }
      };

      const estiloCabecera = {
        font: { bold: true, color: { argb: 'FFFFFF' } },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: '3498DB' } },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      const estiloCelda = {
        alignment: { vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        }
      };

      // 1. Título principal
      worksheet.mergeCells(1, 1, 1, periodosOrdenados.length + 7);
      const celdaTitulo = worksheet.getCell('A1');
      celdaTitulo.style = estiloTitulo;
      worksheet.addRow([]); // Fila vacía


      // 3. Métricas
      const filaMetricasTitulo = worksheet.addRow(['MÉTRICAS']);
      filaMetricasTitulo.getCell(1).style = estiloSubTitulo;
      worksheet.mergeCells(filaMetricasTitulo.number, 1, filaMetricasTitulo.number, 3);
      
      const metricasData = [
        ['Total Participantes', metricas.total],
        ['Visitas Proyectadas', metricas.metaGeneral],
        ['Visitas Realizadas', metricas.archivosEjecutados],
        ['% de Avance', `${metricas.porcentajeAvance}%`]
      ];
      
      metricasData.forEach(([label, valor]) => {
        const fila = worksheet.addRow([label, valor]);
        fila.getCell(1).style = { ...estiloCelda, font: { bold: true } };
        fila.getCell(2).style = estiloCelda;
      });
      
      worksheet.addRow([]); // Fila vacía
      worksheet.addRow([]); // Otra fila vacía

      // 4. Cabecera de la tabla
      const cabecera = [
        '#',
        'Nombre Participante',
        'Cédula',
        'Departamento',
        'Municipio',
        'Proyecto',
        ...periodosOrdenados,
        'Total Archivos'
      ];
      
      const filaCabecera = worksheet.addRow(cabecera);
      filaCabecera.eachCell((cell) => {
        cell.style = estiloCabecera;
      });

      // 5. Datos de participantes
      participantesFiltrados.forEach((participante, index) => {
        const fila = [
          index + 1,
          participante.nombre || '',
          participante.cedula || '',
          participante.departamento || '',
          participante.municipio || '',
          participante.proyectos || '',
        ];

        // Agregar archivos por período
        periodosOrdenados.forEach((periodo) => {
          let cantidad = 0;
          if (participante.resultados) {
            for (const proyecto of Object.values(participante.resultados)) {
              const subcarpetas = proyecto.subcarpetas || {};
              for (const [key, val] of Object.entries(subcarpetas)) {
                const keyLimpio = key.replace(/^\d+\.\s*Visitas\s*/i, "");
                if (keyLimpio === periodo) {
                  cantidad = typeof val === "number" ? val : 0;
                  break;
                }
              }
              if (cantidad > 0) break;
            }
          }
          fila.push(cantidad);
        });

        // Total archivos
        const totalArchivos = Object.values(participante.resultados || {}).reduce(
          (sum, proyecto) => sum + (proyecto.total || 0),
          0
        );
        fila.push(totalArchivos);

        const filaDatos = worksheet.addRow(fila);
        filaDatos.eachCell((cell) => {
          cell.style = estiloCelda;
        });
      });

      // 6. Totales por período (si hay datos)
      if (periodosOrdenados.length > 0 && participantesFiltrados.length > 0) {
        const filaTotales = ['TOTALES', '', '', '', '', ''];
        
        periodosOrdenados.forEach((periodo) => {
          const totalPeriodo = participantesFiltrados.reduce((sum, participante) => {
            let cantidad = 0;
            if (participante.resultados) {
              for (const proyecto of Object.values(participante.resultados)) {
                const subcarpetas = proyecto.subcarpetas || {};
                for (const [key, val] of Object.entries(subcarpetas)) {
                  const keyLimpio = key.replace(/^\d+\.\s*Visitas\s*/i, "");
                  if (keyLimpio === periodo) {
                    cantidad = typeof val === "number" ? val : 0;
                    break;
                  }
                }
                if (cantidad > 0) break;
              }
            }
            return sum + cantidad;
          }, 0);
          filaTotales.push(totalPeriodo);
        });
        
        filaTotales.push(metricas.archivosEjecutados);
        
        const filaTotal = worksheet.addRow(filaTotales);
        filaTotal.eachCell((cell) => {
          cell.style = {
            ...estiloCelda,
            font: { bold: true },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F8F9F9' } }
          };
        });
      }

      // 7. Pie de página
      worksheet.addRow([]);
      const fecha = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      const pieFila = worksheet.addRow([`Generado el: ${fecha}`]);
      worksheet.mergeCells(pieFila.number, 1, pieFila.number, periodosOrdenados.length + 7);
      pieFila.getCell(1).style = {
        font: { italic: true, color: { argb: '7F8C8D' } },
        alignment: { horizontal: 'right' }
      };

      // Ajustar anchos de columna automáticamente
      worksheet.columns.forEach((column, i) => {
        let maxLength = 0;
        worksheet.eachRow({ includeEmpty: false }, (row) => {
          const cellLength = row.getCell(i + 1).value ? row.getCell(i + 1).value.toString().length : 0;
          if (cellLength > maxLength) {
            maxLength = cellLength;
          }
        });
        column.width = Math.min(Math.max(maxLength + 2, 10), 50);
      });

      // Generar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.href = url;
      link.download = `Reporte_Participantes_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Limpiar URL
      setTimeout(() => URL.revokeObjectURL(url), 100);
      
    } catch (error) {
      console.error('Error al exportar:', error);
      alert('Error al exportar el archivo. Por favor, intente de nuevo.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Button
      type="primary"
      icon={<DownloadOutlined />}
      onClick={exportarAExcel}
      loading={exporting}
      disabled={participantesFiltrados.length === 0}
      title="Exportar a Excel"
    >
      Exportar Excel
    </Button>
  );
};

export default ExportarCSV;